import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Moon,
  Sun,
  Menu,
  X,
  LogOut,
  Settings,
  Shield,
  FolderOpen,
  Sparkles,
  ChevronDown,
  FileText,
  MessageSquareText,
  ClipboardCheck,
  BookOpenCheck,
  KeyRound,
  Users,
  Star,
  Lock,
  ArrowUpRight,
  CreditCard,
  LayoutDashboard,
  Facebook,
  Github,
  Instagram,
  Linkedin,
  Youtube,
  AtSign,
  Gift,
  Trophy,
} from 'lucide-react';
import SearchBar from './SearchBar';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import SiteBanner, { useSiteConfig } from './SiteBanner';
import LeadCapture from './LeadCapture';
import AiGateHost from './ai/AiGateHost';
import ConsentBanner, { useGtmId } from './ConsentBanner';
import { openConsentSettings } from '../lib/consent';
import { DotPattern, PosterStripe } from './motion';
import { COMPANY, COMPANY_ADDRESS } from '../lib/company';

const NAV = [
  { to: '/chapters', label: 'Browse' },
  { to: '/index', label: 'A–Z Index' },
  { to: '/code-sets', label: 'Code Sets' },
  { to: '/gems', label: 'GEMs' },
  { to: '/updates', label: 'FY Updates' },
];

const AI_NAV = [
  { to: '/ai/note-to-codes', label: 'Note → Codes', desc: 'Paste a clinical note, get cited code suggestions.', icon: FileText, tone: 'text-pop bg-pop-soft' },
  { to: '/ai/query-builder', label: 'Query Builder', desc: 'Draft compliant physician queries in seconds.', icon: MessageSquareText, tone: 'text-primary bg-sky-soft' },
  { to: '/ai/medical-necessity', label: 'Medical Necessity', desc: 'Check diagnosis support for a procedure.', icon: ClipboardCheck, tone: 'text-ok bg-ok-soft' },
  { to: '/ai/guidelines-chat', label: 'Guidelines Chat', desc: 'Ask the Official Guidelines, with citations.', icon: BookOpenCheck, tone: 'text-warn bg-warn-soft' },
];

const EASE = [0.16, 1, 0.3, 1];
const panelMotion = {
  initial: { opacity: 0, y: -6, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.2, ease: EASE } },
  exit: { opacity: 0, y: -4, scale: 0.98, transition: { duration: 0.12 } },
};

export function LogoMark({ className = 'h-9 w-9' }) {
  return (
    <span className={`relative grid shrink-0 place-items-center overflow-hidden rounded-xl bg-primary ring-1 ring-on-navy/15 ${className}`}>
      <span aria-hidden="true" className="absolute -right-1.5 -top-1.5 h-3.5 w-3.5 rounded-full bg-pop" />
      <svg viewBox="0 0 32 32" className="relative h-[60%] w-[60%]" aria-hidden="true">
        <circle cx="13" cy="13" r="7.5" fill="none" stroke="rgb(var(--c-sun))" strokeWidth="3.5" />
        <path d="M19 19 25.5 25.5" stroke="rgb(var(--c-pop))" strokeWidth="4" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function Logo() {
  return (
    <Link to="/" className="group flex shrink-0 items-center gap-2.5 no-underline" aria-label="ICD Code Finder home">
      <LogoMark className="h-9 w-9 transition-transform duration-300 group-hover:rotate-[-6deg] group-hover:scale-105" />
      <span className="font-display text-[17px] font-extrabold leading-none tracking-tight text-on-navy">
        ICD <span className="text-sun">Code</span> <span className="text-pop">Finder</span>
      </span>
    </Link>
  );
}

function useDismiss(ref, open, onClose) {
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, open, onClose]);
}

const triggerCls = 'flex h-9 items-center gap-1.5 whitespace-nowrap rounded-full px-3 text-[14.5px] font-semibold text-on-navy/80 transition-colors hover:bg-on-navy/[0.07] hover:text-on-navy focus-visible:outline-sun';

function Dropdown({ label, icon: Icon, align = 'right', panelClassName = 'w-64', active = false, children }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();
  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);
  useEffect(() => setOpen(false), [pathname]);
  const pos = align === 'center' ? 'left-1/2 -translate-x-1/2' : align === 'left' ? 'left-0' : 'right-0';
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} className={`${triggerCls} ${open || active ? 'text-on-navy' : ''}`} aria-expanded={open} aria-haspopup="true">
        {active && <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-full bg-on-navy/[0.12] ring-1 ring-on-navy/15" transition={{ type: 'spring', stiffness: 420, damping: 36 }} />}
        {Icon && <Icon className="relative h-4 w-4 text-sun" aria-hidden="true" />}
        <span className="relative">{label}</span>
        <ChevronDown className={`relative h-3.5 w-3.5 opacity-70 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <div className={`absolute top-full z-50 pt-2 ${pos}`}>
        <AnimatePresence>
          {open && (
            <motion.div {...panelMotion} style={{ transformOrigin: align === 'right' ? 'top right' : 'top center' }} className={`overflow-hidden rounded-2xl border border-line/25 bg-surface text-ink shadow-lift ${panelClassName}`}>
              {children}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

const menuItem = 'flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm font-medium text-ink no-underline transition-colors hover:bg-surface-2';

function AiMegaMenu({ active }) {
  return (
    <Dropdown label="AI Tools" icon={Sparkles} align="center" panelClassName="w-[34rem]" active={active}>
      <div className="grid grid-cols-2 gap-1 p-2">
        {AI_NAV.map((n) => {
          const Icon = n.icon;
          return (
            <Link key={n.to} to={n.to} className="group flex gap-3 rounded-xl p-3 no-underline transition-colors hover:bg-surface-2">
              <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${n.tone}`}>
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1 font-display text-sm font-bold text-ink">
                  {n.label}
                  <ArrowUpRight className="h-3.5 w-3.5 -translate-x-1 opacity-0 transition duration-200 group-hover:translate-x-0 group-hover:opacity-100" aria-hidden="true" />
                </span>
                <span className="mt-0.5 block text-[13px] leading-snug text-muted">{n.desc}</span>
              </span>
            </Link>
          );
        })}
      </div>
      <Link to="/settings" className="group flex items-center gap-3 border-t border-line/15 bg-surface-2/70 px-5 py-3 text-sm no-underline transition-colors hover:bg-surface-2">
        <KeyRound className="h-4 w-4 text-pop" aria-hidden="true" />
        <span className="font-semibold text-ink">Bring your own key</span>
        <span className="truncate text-muted">— OpenAI, Anthropic, Gemini or OpenRouter.</span>
        <ArrowUpRight className="ml-auto h-4 w-4 shrink-0 text-muted transition group-hover:text-primary" aria-hidden="true" />
      </Link>
    </Dropdown>
  );
}

const ACCOUNT_LINKS = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/dashboard/codes', label: 'My codes', icon: Star },
  { to: '/dashboard/code-sets', label: 'Code sets', icon: FolderOpen },
  { to: '/dashboard/ai', label: 'AI credits', icon: Sparkles },
  { to: '/dashboard/referrals', label: 'Invite & earn', icon: Gift },
  { to: '/challenge', label: 'Daily challenge', icon: Trophy },
  { to: '/dashboard/billing', label: 'Billing', icon: CreditCard },
  { to: '/dashboard/profile', label: 'Settings', icon: Settings },
];

function AccountLinks({ user, plan, onSignOut, itemCls = menuItem }) {
  return (
    <>
      {ACCOUNT_LINKS.map((n) => {
        const Icon = n.icon;
        return (
          <Link key={n.to} to={n.to} className={itemCls}>
            <Icon className="h-4 w-4 text-muted" aria-hidden="true" /> {n.label}
          </Link>
        );
      })}
      {(plan === 'team' || user.teamId) && (
        <Link to="/dashboard/team" className={itemCls}>
          <Users className="h-4 w-4 text-muted" aria-hidden="true" /> Team
        </Link>
      )}
      {user.role === 'admin' && (
        <Link to="/admin" className={itemCls}>
          <Shield className="h-4 w-4 text-muted" aria-hidden="true" /> Admin
        </Link>
      )}
      <button type="button" className={`${itemCls} w-full text-left`} onClick={onSignOut}>
        <LogOut className="h-4 w-4 text-muted" aria-hidden="true" /> Sign out
      </button>
    </>
  );
}

function Avatar({ user, className = 'h-7 w-7 text-xs' }) {
  const initial = (user.name || user.email || '?').trim().charAt(0).toUpperCase();
  return <span className={`grid shrink-0 place-items-center rounded-full bg-pop font-bold text-navy ring-2 ring-on-navy/20 ${className}`}>{initial}</span>;
}

function AccountMenu() {
  const { user, logout, plan } = useAuth();
  const navigate = useNavigate();
  if (!user) {
    return (
      <div className="flex items-center gap-1.5">
        <Link to="/login" className={`${triggerCls} no-underline`}>
          Sign in
        </Link>
        <Link to="/register" className="btn-pop btn-sm">
          Get started
        </Link>
      </div>
    );
  }
  const signOut = async () => {
    await logout();
    navigate('/');
  };
  return (
    <Dropdown
      label={
        <span className="flex items-center gap-2">
          <Avatar user={user} />
          <span className="hidden max-w-[7.5rem] truncate lg:inline">{user.name || user.email.split('@')[0]}</span>
        </span>
      }
      panelClassName="w-64"
    >
      <div className="border-b border-line/15 px-4 py-3">
        <div className="truncate text-sm font-semibold text-ink">{user.name || user.email.split('@')[0]}</div>
        <div className="truncate text-xs text-muted">{user.email}</div>
        <span className="mt-2 inline-flex rounded-full bg-sky-soft px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-primary">{plan} plan</span>
      </div>
      <div className="p-1.5">
        <AccountLinks user={user} plan={plan} onSignOut={signOut} />
      </div>
    </Dropdown>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  const dark = theme === 'dark';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-full text-sun transition-colors hover:bg-on-navy/[0.08] focus-visible:outline-sun"
      aria-label={`Switch to ${dark ? 'light' : 'dark'} mode`}
      title={`Switch to ${dark ? 'light' : 'dark'} mode`}
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.span
          key={dark ? 'sun' : 'moon'}
          initial={{ rotate: -90, scale: 0.4, opacity: 0 }}
          animate={{ rotate: 0, scale: 1, opacity: 1 }}
          exit={{ rotate: 90, scale: 0.4, opacity: 0 }}
          transition={{ duration: 0.22, ease: EASE }}
          className="grid place-items-center"
        >
          {dark ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

function useScrolled(threshold = 8) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
}

const mobileLink = ({ isActive }) =>
  `flex items-center gap-3 rounded-xl px-3 py-2.5 text-[15px] font-semibold no-underline transition-colors ${isActive ? 'bg-on-navy/[0.12] text-on-navy' : 'text-on-navy/80 hover:bg-on-navy/[0.06] hover:text-on-navy'} focus-visible:outline-sun`;

function MobileAiGroup() {
  const [expanded, setExpanded] = useState(false);
  const { pathname } = useLocation();
  const active = pathname.startsWith('/ai/') || pathname === '/settings';
  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="mobile-ai-tools"
        className={`flex min-h-[44px] w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.16em] transition-colors hover:bg-on-navy/[0.06] hover:text-on-navy focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sun ${expanded || active ? 'text-on-navy/80' : 'text-on-navy/60'}`}
      >
        <Sparkles className="h-3.5 w-3.5 text-sun" aria-hidden="true" />
        <span className="flex-1">AI Tools</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      <div id="mobile-ai-tools">
        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="ai-tools"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1, transition: { duration: 0.22, ease: EASE } }}
              exit={{ height: 0, opacity: 0, transition: { duration: 0.15, ease: EASE } }}
              className="overflow-hidden"
            >
              <div className="grid gap-0.5 pb-1 pl-4 pt-0.5">
                {[...AI_NAV, { to: '/settings', label: 'AI key settings', icon: KeyRound }].map((n) => {
                  const Icon = n.icon;
                  return (
                    <NavLink key={n.to} to={n.to} className={mobileLink}>
                      <Icon className="h-4 w-4 text-sun" aria-hidden="true" />
                      {n.label}
                    </NavLink>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

function MobilePanel({ open, offset }) {
  const { user, logout, plan } = useAuth();
  const navigate = useNavigate();
  // The drawer never grows past the viewport (below the sticky header rows): links scroll, the account actions stay pinned.
  const maxH = offset === 'search' ? 'max-h-[calc(100vh-7.5rem)] supports-[height:100dvh]:max-h-[calc(100dvh-7.5rem)]' : 'max-h-[calc(100vh-4rem)] supports-[height:100dvh]:max-h-[calc(100dvh-4rem)]';
  return (
    <AnimatePresence initial={false}>
      {open && (
        <motion.div
          key="mobile"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1, transition: { duration: 0.3, ease: EASE } }}
          exit={{ height: 0, opacity: 0, transition: { duration: 0.2, ease: EASE } }}
          className="overflow-hidden border-t border-on-navy/10 xl:hidden"
          id="mobile-nav"
        >
          <div className={`flex flex-col ${maxH}`}>
            <nav className="container-page min-h-0 flex-1 overflow-y-auto overscroll-contain pb-4 pt-4" aria-label="Mobile">
              <div className="grid gap-6 sm:grid-cols-2">
                <div>
                  <div className="mb-2 px-3 text-[11px] font-bold uppercase tracking-[0.16em] text-on-navy/45">Explore</div>
                  <div className="grid gap-0.5">
                    {[...NAV, { to: '/challenge', label: 'Daily Code Challenge' }, { to: '/favorites', label: 'Favorites' }, { to: '/pricing', label: 'Pricing' }, { to: '/contact', label: 'Contact' }].map((n, i) => (
                      <motion.div key={n.to} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0, transition: { delay: 0.03 * i, duration: 0.25 } }}>
                        <NavLink to={n.to} className={mobileLink}>
                          {n.label}
                        </NavLink>
                      </motion.div>
                    ))}
                  </div>
                </div>
                <div className="-mt-2 sm:mt-0">
                  <MobileAiGroup />
                </div>
              </div>
            </nav>
            {/* Pinned account / auth footer: a lighter solid navy panel so it reads apart from the nav links above. */}
            <div className="shrink-0 border-t border-on-navy/15 bg-navy">
              <div className="container-page py-3.5">
                {user ? (
                  <section aria-label="Your account">
                    <div className="mb-2.5 flex items-center gap-3 px-1">
                      <Avatar user={user} className="h-10 w-10 text-sm" />
                      <div className="min-w-0 flex-1">
                        <div className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-sun">Your account</div>
                        <div className="truncate text-[15px] font-semibold leading-tight text-on-navy">{user.name || user.email.split('@')[0]}</div>
                        <div className="truncate text-xs text-on-navy/70">{user.email}</div>
                      </div>
                      <span className="shrink-0 rounded-full bg-on-navy/10 px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-on-navy ring-1 ring-inset ring-on-navy/15">{plan}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-0.5 rounded-2xl bg-navy-deep/60 p-1 ring-1 ring-inset ring-on-navy/10">
                      <AccountLinks
                        user={user}
                        plan={plan}
                        itemCls="flex min-h-[44px] items-center gap-2.5 rounded-xl px-3 py-2 text-[14.5px] font-semibold text-on-navy/85 no-underline transition-colors hover:bg-on-navy/10 hover:text-on-navy focus-visible:outline-sun [&_svg]:text-sun"
                        onSignOut={async () => {
                          await logout();
                          navigate('/');
                        }}
                      />
                    </div>
                  </section>
                ) : (
                  <div>
                    <p className="mb-2.5 px-1 text-[13px] leading-snug text-on-navy/75">
                      <span className="font-semibold text-on-navy">Free account</span> — save codes, build code sets and try the AI tools.
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <Link to="/login" className="btn btn-sm border-on-navy/25 bg-on-navy/10 py-3 text-on-navy hover:bg-on-navy/15 focus-visible:outline-sun">
                        Sign in
                      </Link>
                      <Link to="/register" className="btn-pop btn-sm py-3 focus-visible:outline-sun">
                        Get started free
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const scrolled = useScrolled();
  useEffect(() => setMobileOpen(false), [pathname]);
  const isHome = pathname === '/';
  const aiActive = pathname.startsWith('/ai/');

  return (
    <header
      className={`sticky top-0 z-40 bg-navy-deep text-on-navy transition-shadow duration-300 ${scrolled || mobileOpen || !isHome ? 'shadow-lift' : ''}`}
    >
      <div className={`container-page flex items-center gap-3 transition-[height] duration-300 ${scrolled ? 'h-14' : 'h-16'}`}>
        <Logo />
        <nav className="ml-6 hidden items-center gap-0.5 xl:flex" aria-label="Main">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `${triggerCls} relative no-underline ${isActive ? 'text-on-navy' : ''}`}>
              {({ isActive }) => (
                <>
                  {isActive && <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-full bg-on-navy/[0.12] ring-1 ring-on-navy/15" transition={{ type: 'spring', stiffness: 420, damping: 36 }} />}
                  <span className="relative">{n.label}</span>
                </>
              )}
            </NavLink>
          ))}
          <AiMegaMenu active={aiActive} />
          <NavLink to="/pricing" className={({ isActive }) => `${triggerCls} relative no-underline ${isActive ? 'text-on-navy' : ''}`}>
            {({ isActive }) => (
              <>
                {isActive && <motion.span layoutId="nav-pill" className="absolute inset-0 rounded-full bg-on-navy/[0.12] ring-1 ring-on-navy/15" transition={{ type: 'spring', stiffness: 420, damping: 36 }} />}
                <span className="relative">Pricing</span>
              </>
            )}
          </NavLink>
        </nav>
        <div className="ml-auto flex items-center gap-1">
          <ThemeToggle />
          <div className="hidden sm:block">
            <AccountMenu />
          </div>
          <button
            type="button"
            className="grid h-10 w-10 place-items-center rounded-full text-on-navy transition-colors hover:bg-on-navy/[0.08] focus-visible:outline-sun xl:hidden"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-nav"
          >
            <AnimatePresence initial={false} mode="wait">
              <motion.span key={mobileOpen ? 'x' : 'menu'} initial={{ rotate: -45, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 45, opacity: 0 }} transition={{ duration: 0.15 }} className="grid place-items-center">
                {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </motion.span>
            </AnimatePresence>
          </button>
        </div>
      </div>
      {!isHome && (
        <div className="border-t border-on-navy/10">
          <div className="container-page flex items-center gap-4 py-2.5">
            <div className="w-full max-w-2xl">
              <SearchBar size="sm" />
            </div>
            <div className="hidden shrink-0 items-center gap-2 text-xs text-on-navy/50 lg:flex">
              <span className="inline-flex items-center gap-1.5">
                <Lock className="h-3.5 w-3.5 text-sun/80" aria-hidden="true" /> No PHI stored
              </span>
              <span className="h-1 w-1 rounded-full bg-on-navy/25" />
              <span>
                Press <kbd className="kbd border-on-navy/20 bg-on-navy/5 text-on-navy/80 shadow-none">/</kbd> to search
              </span>
            </div>
          </div>
        </div>
      )}
      <MobilePanel open={mobileOpen} offset={isHome ? 'row' : 'search'} />
      {/* Separation from navy page sections below: hairline + the solid three-colour poster stripe (matches /blog). */}
      <div aria-hidden="true" className="flex h-1 border-t border-on-navy/10">
        <span className="flex-[3] bg-primary" />
        <span className="flex-1 bg-sun" />
        <span className="flex-[2] bg-pop" />
      </div>
    </header>
  );
}

const FOOTER_COLS = [
  { title: 'Explore', links: [...NAV, { to: '/challenge', label: 'Daily Code Challenge' }, { to: '/favorites', label: 'Favorites' }, { to: '/search', label: 'Search' }] },
  { title: 'AI Tools', links: [...AI_NAV, { to: '/settings', label: 'AI key settings' }] },
  {
    title: 'Company',
    links: [
      { to: '/pricing', label: 'Pricing' },
      { to: '/blog', label: 'Blog', external: true },
      { to: '/guides', label: 'Guides', external: true },
      { to: '/contact', label: 'Contact us' },
      { to: '/privacy', label: 'Privacy & BYOK keys' },
      { to: '/terms', label: 'Terms of use' },
      { to: '/register', label: 'Create account' },
    ],
  },
];

/* Footer social links: only networks set in admin → Settings → Site & SEO (GET /api/site-config `social`). */
function BrandSvg({ children, className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {children}
    </svg>
  );
}
const XIcon = ({ className }) => (
  <BrandSvg className={className}>
    <path d="M4 4h4.5L20 20h-4.5z" />
    <path d="M20 4l-6.6 7.4M10.6 12.6 4 20" />
  </BrandSvg>
);
const TikTokIcon = ({ className }) => (
  <BrandSvg className={className}>
    <path d="M14 3v11.5a3.5 3.5 0 1 1-3.5-3.5" />
    <path d="M14 3c.4 2.6 2.2 4.4 5 4.6" />
  </BrandSvg>
);
const MastodonIcon = ({ className }) => (
  <BrandSvg className={className}>
    <path d="M20 9.5C20 5.4 17.2 4 12 4S4 5.4 4 9.5v3C4 17.9 7.5 20 12 20c1.6 0 2.9-.2 4-.6v-2c-1.2.4-2.6.6-4 .5-2 0-3.4-.8-3.7-2.4 3.6.8 7 .5 9.4-.3C19.4 14.5 20 13 20 11.5z" />
    <path d="M9 13V9.5a1.5 1.5 0 0 1 3 0V12M12 9.5a1.5 1.5 0 0 1 3 0V13" />
  </BrandSvg>
);
const BlueskyIcon = ({ className }) => (
  <BrandSvg className={className}>
    <path d="M12 11C10.5 7.8 6.8 4 4.5 4 3.4 4 3 4.8 3 6.2c0 1.5.8 5.3 2.6 5.9 1.8.6 3.4.2 3.4.2s-3.6.9-2.6 3.3C7.8 19 11 17.4 12 14c1 3.4 4.2 5 5.6 1.6 1-2.4-2.6-3.3-2.6-3.3s1.6.4 3.4-.2C20.2 11.5 21 7.7 21 6.2 21 4.8 20.6 4 19.5 4 17.2 4 13.5 7.8 12 11z" />
  </BrandSvg>
);

const SOCIAL_NETWORKS = [
  { key: 'x', label: 'X (Twitter)', icon: XIcon },
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin },
  { key: 'facebook', label: 'Facebook', icon: Facebook },
  { key: 'youtube', label: 'YouTube', icon: Youtube },
  { key: 'instagram', label: 'Instagram', icon: Instagram },
  { key: 'github', label: 'GitHub', icon: Github },
  { key: 'tiktok', label: 'TikTok', icon: TikTokIcon },
  { key: 'threads', label: 'Threads', icon: AtSign },
  { key: 'mastodon', label: 'Mastodon', icon: MastodonIcon },
  { key: 'bluesky', label: 'Bluesky', icon: BlueskyIcon },
];

function SocialLinks({ social }) {
  const items = SOCIAL_NETWORKS.filter((n) => social && typeof social[n.key] === 'string' && /^https:\/\/\S+$/i.test(social[n.key].trim()));
  if (!items.length) return null;
  return (
    <ul className="mt-5 flex flex-wrap gap-2" aria-label="ICD Code Finder on social media">
      {items.map(({ key, label, icon: Icon }) => (
        <li key={key}>
          <a
            href={social[key].trim()}
            target="_blank"
            rel="me noopener noreferrer"
            aria-label={`ICD Code Finder on ${label}`}
            title={label}
            className="grid h-9 w-9 place-items-center rounded-full border border-on-navy/15 bg-on-navy/[0.06] text-on-navy/80 transition-colors hover:border-on-navy/30 hover:bg-on-navy/[0.12] hover:text-on-navy"
          >
            <Icon className="h-4 w-4" />
          </a>
        </li>
      ))}
    </ul>
  );
}

function Footer({ flush }) {
  const siteConfig = useSiteConfig();
  const fiscalYear = (siteConfig && siteConfig.fiscalYear) || null;
  const gtmId = useGtmId();
  return (
    <footer className={`relative isolate overflow-hidden bg-navy-deep text-on-navy/80 ${flush ? '' : 'mt-20'}`}>
      <div aria-hidden="true" className="pointer-events-none absolute inset-0">
        <DotPattern className="text-on-navy/[0.07]" />
        <div className="absolute -right-32 -top-32 h-80 w-80 rounded-full bg-electric/20" />
      </div>
      <PosterStripe className="relative" />
      <div className="container-page relative grid gap-10 py-14 md:grid-cols-12">
        <div className="md:col-span-5">
          <Link to="/" className="inline-flex items-center gap-2.5 no-underline" aria-label="ICD Code Finder home">
            <LogoMark className="h-10 w-10" />
            <span className="font-display text-xl font-extrabold text-on-navy">
              ICD <span className="text-sun">Code</span> <span className="text-pop">Finder</span>
            </span>
          </Link>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-on-navy/65">Fast, precise ICD-10-CM lookup with instructional notes, code set checks and AI coding tools included — built for professional coders.</p>
          <SocialLinks social={siteConfig && siteConfig.social} />
          <LeadCapture />
          <div className="mt-5 rounded-2xl border border-on-navy/10 bg-on-navy/[0.05] p-4 text-[13px] leading-relaxed text-on-navy/70">
            <b className="text-sun">Disclaimer:</b> ICD Code Finder is an informational tool and is not a substitute for professional coding judgment, payer policy, or the official ICD-10-CM classification and
            guidelines. Not affiliated with, endorsed by, or sponsored by CMS, NCHS, or the World Health Organization. AI suggestions require review by a qualified coder.
          </div>
        </div>
        {FOOTER_COLS.map((col, i) => (
          <div key={col.title} className={i === 1 ? 'md:col-span-3' : 'md:col-span-2'}>
            <div className="font-display text-xs font-bold uppercase tracking-[0.16em] text-on-navy/45">{col.title}</div>
            <ul className="mt-4 space-y-2.5 text-sm">
              {col.links.map((n) => (
                <li key={n.to}>
                  {n.external ? (
                    <a href={n.to} className="link-underline text-on-navy/75 transition-colors hover:text-on-navy">
                      {n.label}
                    </a>
                  ) : (
                    <Link to={n.to} className="link-underline text-on-navy/75 transition-colors hover:text-on-navy">
                      {n.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="relative border-t border-on-navy/10">
        <div className="container-page flex flex-col items-center justify-between gap-3 py-5 text-xs text-on-navy/50 sm:flex-row">
          <span className="space-y-1 text-center sm:text-left">
            <span className="block">
              © {new Date().getFullYear()} {COMPANY.name} · ICD Code Finder · ICD-10-CM data from public CMS/NCHS releases.
            </span>
            <span className="flex flex-wrap justify-center gap-x-2 gap-y-1 sm:justify-start">
              <span className="whitespace-nowrap">{COMPANY_ADDRESS}</span>
              <span aria-hidden="true">·</span>
              <a href={`mailto:${COMPANY.email}`} className="whitespace-nowrap text-on-navy/70 hover:text-on-navy">{COMPANY.email}</a>
              <span aria-hidden="true">·</span>
              <a href={COMPANY.phoneHref} className="whitespace-nowrap text-on-navy/70 hover:text-on-navy">{COMPANY.phone}</a>
              {gtmId && (
                <>
                  <span aria-hidden="true">·</span>
                  <button type="button" onClick={openConsentSettings} className="whitespace-nowrap text-on-navy/70 underline-offset-2 hover:text-on-navy hover:underline">
                    Cookie settings
                  </button>
                </>
              )}
            </span>
          </span>
          <span className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-ok opacity-60" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-ok" />
              </span>
              {fiscalYear ? `FY${fiscalYear} code set` : 'ICD-10-CM code set'}
            </span>
            <span className="h-1 w-1 rounded-full bg-on-navy/25" />
            <span>
              Press <kbd className="kbd border-on-navy/20 bg-on-navy/5 text-on-navy/80 shadow-none">/</kbd> to search
            </span>
          </span>
        </div>
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  return (
    <div className="flex min-h-screen flex-col">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-xl focus:bg-sun focus:px-4 focus:py-2 focus:font-semibold focus:text-sun-ink">
        Skip to content
      </a>
      <SiteBanner />
      <Header />
      <main id="main" className="flex-1">
        {children}
      </main>
      <Footer flush={pathname === '/' || pathname.startsWith('/dashboard')} />
      {/* Hosted-AI consent / limit dialogs for any page that calls the AI API (e.g. semantic search). */}
      <AiGateHost />
      <ConsentBanner />
    </div>
  );
}
